import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { DocumentEntityType, DocumentType } from "../../../../../src/api/caseDocuments";
import { LEAType } from "../../../../../src/api/events";
import { LeaReportsData } from "../../../../../src/api/lea";
import { useAppDispatch, useAppSelector } from "../../../../../src/app/store";
import ButtonV2 from "../../../../../src/components/ui/button/v2";
import { cn } from "../../../../../src/lib/utils";
import { SaveButtonV2 } from "../../../../components/ui/button/v2/SaveButton";
import { isCaseDocumentsLoading } from "../../../../features/documentManagement/logic/caseDocumentsSlice";
import { isEventsDataLoading } from "../../../../features/timeline/logic/slice";
import ReportDocuments from "../../../documentManagement/components/ReportDocuments";
import { AddEventModal } from "../../../timeline/components/AddEventModal";
import { selectLeaReportByType } from "../../logic/leaSlice";
import {
  fetchLeaReportsThunk,
  upsertLeaReportsThunk,
} from "../../logic/leaThunk";
import {
  ReportDetailsShimmering,
  ShimmerBlock,
} from "../common/ReportDetailsShimmering";
import ReportFormTable from "../common/ReportTableForm";
import ReportToggleSection from "../common/ReportToggleSection";
import styles from "../ro/components/ROPage.module.css";
import { useLeaDetailsData } from "./LeaDetails";
import LeaReportHeader from "./LeaReportHeader";

const LeaReportPage: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();

  const statePoliceData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.STATE_POLICE)
  );
  const cbiData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.CBI)
  );
  const cyberPoliceData = useAppSelector((state) =>
    selectLeaReportByType(state, LEAType.CYBER_POLICE)
  );

  const [isStatePoliceOn, setIsStatePoliceOn] = useState(false);
  const [isCBIOn, setIsCBIOn] = useState(false);
  const [isCyberPoliceOn, setIsCyberPoliceOn] = useState(false);

  const [discardTrigger, setDiscardTrigger] = useState<number>(0);
  const [dirtyLeaData, setDirtyLeaData] = useState<LeaReportsData | undefined>(
    undefined
  );

  const [isSavingLocal, setIsSavingLocal] = useState(false);
  const [isLeaLoading, setIsLeaLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const eventsDataLoading = useAppSelector(isEventsDataLoading);
  const documentsLoading = useAppSelector(isCaseDocumentsLoading);

  useEffect(() => {
    setIsPageLoading(isLeaLoading || eventsDataLoading || documentsLoading);
  }, [isLeaLoading, eventsDataLoading, documentsLoading])

  const isStatePoliceLocked = isStatePoliceOn && statePoliceData !== undefined;
  const isCBILocked = isCBIOn && cbiData !== undefined;
  const isCyberPoliceLocked = isCyberPoliceOn && cyberPoliceData !== undefined;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!case_id) return;

    let cancelled = false;

    const load = async () => {
      setIsLeaLoading(true);
      try {
        await Promise.allSettled([
          dispatch(fetchLeaReportsThunk({ case_id })).unwrap(),
        ]);
      } finally {
        if (!cancelled) setIsLeaLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [case_id, dispatch]);

  useEffect(() => {
    if (statePoliceData !== undefined) {
      setIsStatePoliceOn(statePoliceData !== null);
    }
    if (cbiData !== undefined) {
      setIsCBIOn(cbiData !== null);
    }
    if (cyberPoliceData !== undefined) {
      setIsCyberPoliceOn(cyberPoliceData !== null);
    }
  }, [statePoliceData, cbiData, cyberPoliceData]);

  const {
    statePoliceFormRows,
    cbiFormRows,
    cyberPoliceFormRows,
    eventModal,
    setEventModal,
  } = useLeaDetailsData(setDirtyLeaData, discardTrigger);

  const handleSaveLea = async () => {
    if (!case_id || !dirtyLeaData) return;

    try {
      setIsSavingLocal(true);
      await dispatch(
        upsertLeaReportsThunk({
          case_id,
          request: dirtyLeaData,
        })
      ).unwrap();
      await dispatch(fetchLeaReportsThunk({ case_id })).unwrap();
      setDirtyLeaData(undefined);
      setDiscardTrigger((prev) => prev + 1);
    } finally {
      setIsSavingLocal(false);
    }
  };

  const renderDiscardButton = () => (
    <ButtonV2
      variant="outline"
      onClick={() => {
        setDiscardTrigger((prev) => prev + 1);
        setDirtyLeaData(undefined);
      }}
      disabled={!dirtyLeaData || isSavingLocal}
      className={cn(
        "font-sans font-medium text-sm leading-5 w-[75.5px] h-8 border-[#3551F3] text-[#3551F3] py-3",
        (!dirtyLeaData || isSavingLocal) && "opacity-50 cursor-not-allowed"
      )}
    >
      Discard
    </ButtonV2>
  );

  const renderSaveButton = () => (
     <SaveButtonV2
     disabled={!dirtyLeaData || isSavingLocal}
     onClick={handleSaveLea}
     buttonText={isSavingLocal ? "Saving..." : "Save"}
   />
  );

  const renderLeaReportsContainer = () => (
    <>
      <LeaReportHeader />
      <div className="px-6 py-4 flex flex-col">
        <ReportToggleSection
          label="Reporting to State / UT Police"
          isOn={isStatePoliceOn}
          onToggle={() => setIsStatePoliceOn((v) => !v)}
          disabled={isStatePoliceLocked}
        >
          <ReportFormTable rows={statePoliceFormRows} className="mt-4" />
          <ReportDocuments
            document_type={DocumentType.LEA_REPORT}
            entityId={LEAType.STATE_POLICE}
            entityType={DocumentEntityType.LEA}
          />
        </ReportToggleSection>

        <ReportToggleSection
          label="Reporting to CBI"
          isOn={isCBIOn}
          onToggle={() => setIsCBIOn((v) => !v)}
          disabled={isCBILocked}
        >
          <ReportFormTable rows={cbiFormRows} className="mt-4" />
          <ReportDocuments
            document_type={DocumentType.LEA_REPORT}
            entityId={LEAType.CBI}
            entityType={DocumentEntityType.LEA}
          />
        </ReportToggleSection>

        <ReportToggleSection
          label="Reporting to Cyber Police"
          isOn={isCyberPoliceOn}
          onToggle={() => setIsCyberPoliceOn((v) => !v)}
          showDivider={false}
          disabled={isCyberPoliceLocked}
        >
          <ReportFormTable rows={cyberPoliceFormRows} className="mt-4" />
          <ReportDocuments
            document_type={DocumentType.LEA_REPORT}
            entityId={LEAType.CYBER_POLICE}
            entityType={DocumentEntityType.LEA}
          />
        </ReportToggleSection>
      </div>
    </>
  );

  return (
    <>
      <div
        className={cn(styles.topHeader, "flex items-center justify-end")}
      >
        <div className="flex gap-3">
          {isPageLoading ? (
            <div className="flex gap-3 animate-pulse">
              <ShimmerBlock className="h-8 w-[75.5px]" />
              <ShimmerBlock className="h-8 w-[75.5px]" />
            </div>
          ) : (
            <>
              {renderDiscardButton()}
              {renderSaveButton()}
            </>
          )}
        </div>
      </div>

      <div className={styles.pageContainer}>
        <div className={styles.mainContent}>
          <div className="-mx-6">
            <div className="mx-auto w-[1032px] bg-white rounded-xl overflow-hidden">
              {isPageLoading ? (
                <ReportDetailsShimmering hideTopHeaderLayout sectionCount={3}/>
              ) : (
                renderLeaReportsContainer()
              )}
            </div>
          </div>
        </div>
      </div>

      {eventModal.isOpen && eventModal.eventType && eventModal.entityId && (
        <AddEventModal
          mode="Edit"
          disableStageSelect
          disableEntitySelect
          readOnlyNote
          initialData={{
            eventType: eventModal.eventType,
            events: [{ entity_id: eventModal.entityId, date: "" }],
          }}
          onClose={() => setEventModal({ isOpen: false })}
        />
      )}
    </>
  );
};

export default LeaReportPage;
